import { useMutation } from "react-query";
import axios from "axios";
import { useToast } from "../useToast";
import { useAuth0 } from "@auth0/auth0-react";

/**
 * Profile picture upload hook
 * Uploads via backend API for better security and control
 */
export const useUpdateProfilePicture = () => {
  const { getAccessTokenSilently } = useAuth0();
  const toast = useToast();
  const apiUrl = process.env.REACT_APP_API_URL;

  return useMutation(
    async ({ userId, imageFile }) => {
      try {
        console.log("Starting profile picture upload via backend...", {
          userId,
          fileSize: imageFile.size,
          fileType: imageFile.type,
        });

        // Get access token for API authentication
        const token = await getAccessTokenSilently({ cacheMode: "off" });

        // Create FormData to send the file
        const formData = new FormData();
        formData.append("profilePicture", imageFile);
        formData.append("userId", userId);

        // Upload to backend API
        const response = await axios.post(
          `${apiUrl}/api/users/upload-profile-picture`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
            // Track upload progress
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              console.log(`Upload progress: ${percentCompleted}%`);
            },
          },
        );

        console.log("Profile picture uploaded successfully:", response.data);
        return response.data;
      } catch (error) {
        console.error("Profile picture upload error:", error);

        // Handle specific error cases
        if (error.response?.status === 413) {
          throw new Error(
            "Image file is too large. Please use a smaller image.",
          );
        }

        if (error.response?.status === 415) {
          throw new Error(
            "Invalid file type. Please upload a valid image (JPG, PNG, GIF).",
          );
        }

        throw new Error(
          error.response?.data?.message ||
            error.message ||
            "Failed to upload profile picture",
        );
      }
    },
    {
      onSuccess: () => {
        toast.success("Profile picture updated successfully");
      },
      onError: (error) => {
        toast.error(error.message);
      },
    },
  );
};
